import React from "react";
import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import RuleIcon from "@mui/icons-material/Rule";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import { CRUD_VERBS, ModelGroup, VERB_LABEL, humanize, modelsPermIds } from "./permissionUtils";

interface PermissionAppListProps {
  visibleGrouped: Record<string, ModelGroup[]>;
  granted: Set<number>;
  expandedApps: Record<string, boolean>;
  toggleApp: (app: string) => void;
  toggleAllPermissionsForApp: (models: ModelGroup[]) => void;
  toggleFullCrudForModel: (m: ModelGroup) => void;
  togglePermission: (id: number) => void;
  isAppFullyGranted: (models: ModelGroup[]) => boolean;
  isAppPartiallyGranted: (models: ModelGroup[]) => boolean;
  accentColor: string;
  maxHeight?: string | number;
  emptyMessage?: string;
}

const PermissionAppList: React.FC<PermissionAppListProps> = ({
  visibleGrouped,
  granted,
  expandedApps,
  toggleApp,
  toggleAllPermissionsForApp,
  toggleFullCrudForModel,
  togglePermission,
  isAppFullyGranted,
  isAppPartiallyGranted,
  accentColor,
  maxHeight,
  emptyMessage = "No permissions match the current search and filters.",
}) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{ minHeight: 300, overflow: "auto", pr: 0.5, maxHeight }}
    >
      {Object.keys(visibleGrouped).length === 0 && (
        <Paper
          sx={{
            boxShadow: 0,
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 3,
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography color="text.secondary">{emptyMessage}</Typography>
        </Paper>
      )}

      {Object.entries(visibleGrouped).map(([app, models]) => {
        const isAllGranted = isAppFullyGranted(models);
        const isPartial = isAppPartiallyGranted(models);
        const ids = modelsPermIds(models);
        const grantedCount = ids.filter((id) => granted.has(id)).length;
        const isExpanded = !!expandedApps[app];

        return (
          <Paper
            key={app}
            sx={{
              boxShadow: 0,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              overflow: "hidden",
              borderLeft: "4px solid",
              borderLeftColor: grantedCount > 0 ? accentColor : "divider",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={1.5}
              px={2}
              py={1.5}
              sx={{
                cursor: "pointer",
                "&:hover": { backgroundColor: "action.hover" },
              }}
              onClick={() => toggleApp(app)}
            >
              <Avatar
                variant="rounded"
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: "action.selected",
                  color: "text.secondary",
                }}
              >
                <LayersOutlinedIcon fontSize="small" />
              </Avatar>
              <Box flexGrow={1} minWidth={0}>
                <Typography fontWeight="bold" noWrap>
                  {humanize(app)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {models.length} model{models.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
              <Chip
                label={`${grantedCount} of ${ids.length} enabled`}
                size="small"
                color={grantedCount > 0 ? "primary" : "default"}
                variant={grantedCount > 0 ? "filled" : "outlined"}
              />
              <Tooltip title="Toggle all permissions in this app">
                <Checkbox
                  checked={isAllGranted}
                  indeterminate={isPartial}
                  onChange={() => toggleAllPermissionsForApp(models)}
                  onClick={(e) => e.stopPropagation()}
                  color="primary"
                />
              </Tooltip>
              <IconButton size="small">
                {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            </Box>

            <Collapse in={isExpanded} timeout="auto" unmountOnExit mountOnEnter>
              <Divider />
              <Box px={2} py={2}>
                {models.map((m, idx) => {
                  const crudIds = CRUD_VERBS.map((v) => m.crud[v]?.id).filter(
                    (id): id is number => id !== undefined,
                  );
                  const isModelFullCrud =
                    crudIds.length > 0 &&
                    crudIds.every((id) => granted.has(id));

                  return (
                    <Box
                      key={m.model}
                      sx={{ mb: idx < models.length - 1 ? 3 : 0 }}
                    >
                      <Box display="flex" alignItems="center" gap={0.75} mb={1}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {humanize(m.model)}
                        </Typography>
                        {m.isEventTwin && (
                          <Chip
                            label="audit"
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: "0.65rem" }}
                          />
                        )}
                        {crudIds.length > 0 && (
                          <Tooltip title="Toggle full CRUD for this model">
                            <IconButton
                              size="small"
                              color={isModelFullCrud ? "success" : "default"}
                              onClick={() => toggleFullCrudForModel(m)}
                            >
                              <RuleIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                            md: "repeat(3, 1fr)",
                          },
                          columnGap: 2,
                          rowGap: 1.5,
                        }}
                      >
                        {CRUD_VERBS.map((verb) => {
                          const perm = m.crud[verb];
                          if (!perm) return null;
                          return (
                            <PermissionTile
                              key={verb}
                              label={`${VERB_LABEL[verb]} ${humanize(m.model)}`}
                              description={perm.name}
                              checked={granted.has(perm.id)}
                              onToggle={() => togglePermission(perm.id)}
                            />
                          );
                        })}
                        {m.other.map((p) => (
                          <PermissionTile
                            key={p.id}
                            label={humanize(p.name)}
                            description={p.codename}
                            checked={granted.has(p.id)}
                            onToggle={() => togglePermission(p.id)}
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Collapse>
          </Paper>
        );
      })}
    </Box>
  );
};

const PermissionTile: React.FC<{
  label: string;
  description?: string;
  checked: boolean;
  onToggle: () => void;
}> = ({ label, description, checked, onToggle }) => (
  <Box
    display="flex"
    alignItems="flex-start"
    gap={1}
    onClick={onToggle}
    sx={{
      cursor: "pointer",
      p: 1,
      borderRadius: 2,
      "&:hover": { backgroundColor: "action.hover" },
    }}
  >
    <Checkbox
      size="small"
      checked={checked}
      onChange={onToggle}
      onClick={(e) => e.stopPropagation()}
      color="primary"
      sx={{ p: 0, mt: 0.3 }}
    />
    <Box minWidth={0}>
      <Typography variant="body2" fontWeight={600} noWrap title={label}>
        {label}
      </Typography>
      {description && (
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
  </Box>
);

export default PermissionAppList;
